/* eslint-disable no-console -- Bootstrap/startup file: console used for critical error handling before app loads */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import MaintenancePage from './components/MaintenancePage.tsx';
import { API_BASE_URL } from './config/api';
import { getFullPageLoaderBootstrapHtml } from './loading/fullPageLoaderBootstrap';
import { isChunkLoadError, shouldReloadForChunkError } from './utils/chunkReload';
import { serializeError } from './utils/errorSerializer';
import { getSentryTunnelUrl } from './utils/sentryTunnelUrl';

import type * as SentryReact from '@sentry/react'; // type-only — erased at build, does NOT pull the runtime chunk
import './index.css';

// Mock Zebra printer in development
import './utils/zebraMock';

declare global {
  interface Window {
    /** Set true the instant this entry module executes; read by public/watchdog.js. */
    __APP_BOOTED__?: boolean;
  }
}

// Signal that the entry script ran. The boot watchdog (public/watchdog.js) uses
// this as the authoritative "the app booted" check — it must NOT rely on #root
// being non-empty, because bootstrap() clears #root and then awaits the App
// chunk over the network, leaving #root momentarily empty on a healthy load.
window.__APP_BOOTED__ = true;

interface MaintenanceStatus {
  enabled: boolean;
  message: string | null;
  environment: 'development' | 'production';
}

/**
 * Lazy Sentry loader.
 *
 * `@sentry/react` is a large (~380KB) dependency. Importing it statically forces
 * the browser to download and evaluate it before the app entry can run at all —
 * if that single chunk is slow or blocked at the edge (e.g. a Cloudflare
 * challenge returning HTML for the .js request during a peak-hour traffic
 * burst), the page renders blank white with no loader and no error UI, because
 * no application code has executed yet.
 *
 * Loading Sentry lazily moves it off the synchronous boot critical path: the app
 * shell can paint even if the Sentry chunk never arrives. Error reporting is
 * best-effort — the loader/init runs in the background and `captureMessageLazy`
 * awaits it before sending, so early chunk-load errors are still reported once
 * Sentry is ready.
 */
type SentryModule = typeof SentryReact;

let sentryReady: Promise<SentryModule> | null = null;

function getSentry(): Promise<SentryModule> {
  sentryReady ??= import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: import.meta.env.MODE === 'production' ? (import.meta.env.VITE_SENTRY_DSN as string | undefined) : undefined,
      tunnel: getSentryTunnelUrl(import.meta.env.MODE, API_BASE_URL),
      environment: import.meta.env.MODE || 'development',

      // Integration configurations
      integrations: [
        // Capture console errors
        Sentry.captureConsoleIntegration({
          levels: ['error', 'warn'],
        }),
      ],

      // Errors only, no performance spans: tracing lives in Axiom, and the Sentry plan's
      // span quota is far below the portal's volume, so frontend spans would be rejected.
      // Trap: browserTracingIntegration plus tracesSampleRate or tracesSampler re-enables them.

      // Release tracking - will be set during build
      release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,

      // Ignore specific errors
      ignoreErrors: [
        // Ignore browser extension errors
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Ignore network errors that are expected
        'NetworkError',
        'Failed to fetch',
        // Ignore expected Zebra Browser Print errors - these occur when the service
        // is not installed or not running, which is normal for most users
        /\[Zebra\] Browser Print service not running/,
        /\[Zebra\] Failed to get printers/,
        /Failed to get printers:/,
      ],

      // Set initial scope
      initialScope: {
        tags: {
          component: 'react-app',
          deployment: 'frontend',
        },
      },

      // Before sending events to Sentry, tag chunk loading errors for easy filtering
      beforeSend(event, _hint) {
        // Tag chunk loading errors for easy filtering in Sentry dashboard
        if (
          event.message?.includes('chunk') ||
          event.message?.includes('MIME type') ||
          event.message?.includes('dynamically imported module')
        ) {
          event.tags = {
            ...event.tags,
            chunk_loading_error: 'true',
          };
        }
        return event;
      },
    });
    return Sentry;
  });
  return sentryReady;
}

/** Fire-and-forget Sentry capture that lazily loads + inits Sentry first. */
function captureMessageLazy(
  message: string,
  options: Parameters<SentryModule['captureMessage']>[1],
): void {
  void getSentry()
    .then((Sentry) => Sentry.captureMessage(message, options))
    .catch(() => {
      /* Sentry unavailable — reporting is best-effort, never block boot. */
    });
}

// Kick off Sentry load + init in the background (does not block the app shell).
void getSentry();

/**
 * Chunk Loading Error Handler
 *
 * Handles cases where lazy-loaded chunks fail to load due to deployment updates.
 * When a user has the app open and a new deployment occurs, chunk hashes change.
 * Attempting to load old chunks results in 404s (which return HTML instead of JS).
 *
 * This handler detects chunk loading failures and reloads once to fetch the new
 * version. The one-reload guard is a timestamp cooldown (see ./utils/chunkReload)
 * that survives the reload — a boolean flag cleared on `load` reload-looped
 * forever, because lazy-route chunk errors fire after `load`. Within the cooldown
 * we stop and let the error propagate to the ErrorBoundary UI.
 *
 * Two handlers are used:
 * 1. unhandledrejection - catches Promise rejections from dynamic imports
 * 2. error - catches other chunk loading errors
 */

/**
 * Handle Promise rejections from dynamic imports
 * Dynamic imports return Promises, so they reject rather than throwing errors
 */
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason as { message?: string; constructor?: { name?: string } } | undefined;

  if (!isChunkLoadError(error?.message)) {
    return;
  }

  const willReload = shouldReloadForChunkError();

  // Track in Sentry before reloading (lazy — Sentry may still be loading)
  captureMessageLazy('[ChunkLoadError] Stale chunk detected (promise rejection)', {
    level: 'warning',
    tags: {
      chunk_error: 'true',
      reload_attempt: willReload ? 'reloading' : 'cooldown-blocked',
      error_source: 'unhandledrejection',
    },
    extra: {
      errorMessage: serializeError(error, 'Unknown chunk load error'),
      errorType: error?.constructor?.name ?? typeof error,
      userAgent: navigator.userAgent,
      url: window.location.href,
    },
  });

  if (willReload) {
    console.warn('[ChunkLoadError] Detected stale chunk via promise rejection, reloading...');
    // Prevent default error handling, then reload to get fresh chunks
    event.preventDefault();
    window.location.reload();
  } else {
    // Reloaded recently and error persists — stop looping, let it surface
    console.error('[ChunkLoadError] Reloaded recently, error persists:', error);
  }
});

/**
 * Handle general window errors from chunk loading
 * Some chunk loading errors may not be Promise rejections
 */
window.addEventListener('error', (event) => {
  if (!isChunkLoadError(event.message)) {
    return;
  }

  const willReload = shouldReloadForChunkError();

  // Track in Sentry before reloading (lazy — Sentry may still be loading)
  captureMessageLazy('[ChunkLoadError] Stale chunk detected (error event)', {
    level: 'warning',
    tags: {
      chunk_error: 'true',
      reload_attempt: willReload ? 'reloading' : 'cooldown-blocked',
      error_source: 'error',
    },
    extra: {
      errorMessage: event.message,
      userAgent: navigator.userAgent,
      url: window.location.href,
    },
  });

  if (willReload) {
    console.warn('[ChunkLoadError] Detected stale chunk, reloading page...');
    // Reload the page to get fresh chunks
    window.location.reload();
  } else {
    // Reloaded recently and error persists — stop looping, let it surface
    console.error('[ChunkLoadError] Reloaded recently, error persists:', event.message);
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

/**
 * Check maintenance mode status from backend API
 * This ensures maintenance mode respects production environment checks
 */
async function checkMaintenanceStatus(): Promise<MaintenanceStatus> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Short timeout to avoid blocking app load too long
      signal: AbortSignal.timeout(3000),
    });

    if (!response.ok) {
      // If API fails, assume maintenance mode is off to avoid blocking users
      console.warn('[Maintenance] Failed to check status, assuming maintenance mode is off');
      return { enabled: false, message: null, environment: 'development' };
    }

    return await response.json() as MaintenanceStatus;
  } catch (error) {
    // On any error (network, timeout, etc), assume maintenance is off
    console.warn('[Maintenance] Error checking status:', error);
    return { enabled: false, message: null, environment: 'development' };
  }
}

/**
 * Bootstrap function - checks maintenance mode before rendering app
 */
async function bootstrap(root: HTMLElement) {
  root.innerHTML = getFullPageLoaderBootstrapHtml();

  // Check maintenance mode
  const maintenanceStatus = await checkMaintenanceStatus();

  // Clear loading indicator
  root.innerHTML = '';

  // Render appropriate component based on maintenance status
  if (maintenanceStatus.enabled) {
    // Maintenance mode: render lightweight page without loading App
    createRoot(root).render(
      <StrictMode>
        <MaintenancePage message={maintenanceStatus.message} />
      </StrictMode>,
    );
  } else {
    // Normal mode: dynamically import App only when needed
    // This prevents loading app modules during maintenance
    const { default: App } = await import('./App.tsx');
    createRoot(root).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  }
}

// Start the app
bootstrap(rootElement).catch(async (error) => {
  console.error('[Bootstrap] Fatal error during app initialization:', error);
  // Fallback: dynamically load and render app anyway if bootstrap fails
  try {
    const { default: App } = await import('./App.tsx');
    createRoot(rootElement).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  } catch (fallbackError) {
    console.error('[Bootstrap] Failed to load fallback app:', fallbackError);
    // Last resort: show error message
    rootElement.innerHTML = `
      <div style="display: flex; align-items: center; justify-center; height: 100vh; background: #F9FCFC; padding: 20px;">
        <div style="text-align: center; max-width: 500px;">
        <div style="color: #c91227; font-size: 3rem; margin-bottom: 16px;">⚠️</div>
          <h1 style="color: #1f2937; font-family: 'Manrope', sans-serif; margin-bottom: 8px;">Något gick fel</h1>
          <p style="color: #6b7280; font-family: 'Manrope', sans-serif;">Kunde inte ladda applikationen. Vänligen ladda om sidan.</p>
        </div>
      </div>
    `;
  }
});
